import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Image,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { CF } from "../theme/turquoiseTheme.js";
import { CinematicHeroCard } from "./library/CinematicHeroCard.js";
import { CinematicScene } from "./library/CinematicScene.js";
import { BookRecommendationsScreen } from "./BookRecommendationsScreen.js";
import { YouTubePlayerModal } from "./library/YouTubePlayerModal.js";
import {
  LIBRARY_ARTICLES,
  getArticlesForLang,
  getPodcastsForLang,
  podcastDisplaySummary,
  podcastDisplayTitle,
} from "../content/libraryCatalog.js";
import { extractYoutubeVideoId, youtubeThumbnailUrl } from "../utils/youtube.js";
import { useLanguage } from "../i18n/LanguageContext.js";
import { touchUI } from "../utils/touchUI.js";

/**
 * @param {{
 *   selectedArticleId: string | null,
 *   onSelectArticle: (id: string | null) => void,
 * }} props
 */
export function LibraryTabPanel({ selectedArticleId, onSelectArticle }) {
  const { locale, setLocale, t } = useLanguage();
  const contentLang = locale === "fr" ? "fr" : "en";
  const [activeVideo, setActiveVideo] = useState(null);
  const articles = useMemo(() => getArticlesForLang(contentLang), [contentLang]);
  const podcasts = useMemo(() => getPodcastsForLang(contentLang), [contentLang]);
  const article = selectedArticleId ? LIBRARY_ARTICLES.find((a) => a.id === selectedArticleId) : null;

  const openArticleUrl = useCallback(async (url) => {
    try {
      const can = await Linking.canOpenURL(url);
      if (can) await Linking.openURL(url);
      else Alert.alert(t("library_link_error_title"), t("library_link_error_body"));
    } catch (e) {
      Alert.alert(t("library_link_error_title"), e?.message || t("library_link_error_body"));
    }
  }, [t]);

  if (article) {
    const { scene } = article;
    return (
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <CinematicScene sceneId={scene.sceneId} height={260} overlay={scene.overlay} />
        <View style={styles.readerBody}>
          <Pressable style={styles.backBtn} onPress={() => onSelectArticle(null)}>
            <Text style={styles.backBtnText}>{t("library_back")}</Text>
          </Pressable>
          <Text style={styles.readerTitle}>{article.title}</Text>
          <View style={styles.metaRow}>
            <Text style={styles.meta}>★ {scene.rating.toFixed(1)}</Text>
            <Text style={styles.meta}>
              {scene.readMin} {t("library_min_read")}
            </Text>
          </View>
          <Text style={styles.readerTagline}>{scene.tagline}</Text>
          <Text style={styles.articleBody}>{article.body}</Text>
          {article.url ? (
            <Pressable style={styles.linkBtn} onPress={() => void openArticleUrl(article.url)}>
              <Text style={styles.linkBtnText}>{t("library_open_link")}</Text>
            </Pressable>
          ) : null}
        </View>
      </ScrollView>
    );
  }

  return (
    <>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <Text style={styles.journeyLabel}>PROCSYSTIM</Text>
        <Text style={styles.title}>{t("library_title")}</Text>
        <Text style={styles.subtitle}>{t("library_subtitle")}</Text>

        <View style={styles.langRow}>
          <Pressable
            style={[styles.langChip, contentLang === "en" && styles.langChipOn]}
            onPress={() => setLocale("en")}
          >
            <Text style={[styles.langChipText, contentLang === "en" && styles.langChipTextOn]}>{t("lang_english")}</Text>
          </Pressable>
          <Pressable
            style={[styles.langChip, contentLang === "fr" && styles.langChipOn]}
            onPress={() => setLocale("fr")}
          >
            <Text style={[styles.langChipText, contentLang === "fr" && styles.langChipTextOn]}>{t("lang_french")}</Text>
          </Pressable>
        </View>

        <Text style={styles.sectionLabel}>{t("library_articles")}</Text>
        {articles.length === 0 ? <Text style={styles.empty}>{t("library_empty_articles")}</Text> : null}
        {articles.map((item) => (
          <CinematicHeroCard
            key={item.id}
            title={item.title}
            tagline={item.scene.tagline}
            sceneId={item.scene.sceneId}
            rating={item.scene.rating}
            readMin={item.scene.readMin}
            mood={item.scene.mood}
            onPress={() => onSelectArticle(item.id)}
          />
        ))}

        <Text style={[styles.sectionLabel, styles.sectionGap]}>{t("library_videos")}</Text>
        {podcasts.length === 0 ? <Text style={styles.empty}>{t("library_empty_videos")}</Text> : null}
        {podcasts.map((item) => {
          const videoId = item.videoId || extractYoutubeVideoId(item.url);
          const title = podcastDisplayTitle(item, contentLang);
          return (
            <Pressable
              key={item.id}
              style={styles.videoCard}
              onPress={() =>
                setActiveVideo({
                  title,
                  channel: item.channel || "YouTube",
                  url: item.url,
                })
              }
            >
              <View style={styles.thumbWrap}>
                {videoId ? (
                  <Image source={{ uri: youtubeThumbnailUrl(videoId) }} style={styles.thumb} />
                ) : (
                  <View style={[styles.thumb, styles.thumbFallback]} />
                )}
                <View style={styles.playBadge}>
                  <Text style={styles.playBadgeText}>▶</Text>
                </View>
              </View>
              <View style={styles.videoMeta}>
                <Text style={styles.videoTitle} numberOfLines={2}>
                  {title}
                </Text>
                <Text style={styles.videoChannel} numberOfLines={1}>
                  {item.channel || "YouTube"}
                </Text>
                <Text style={styles.videoSummary} numberOfLines={2}>
                  {podcastDisplaySummary(item, contentLang)}
                </Text>
              </View>
            </Pressable>
          );
        })}

        <Text style={[styles.sectionLabel, styles.sectionGap]}>{t("library_books")}</Text>
        <BookRecommendationsScreen embedded cinematic />
      </ScrollView>

      <YouTubePlayerModal
        visible={!!activeVideo}
        title={activeVideo?.title || ""}
        channel={activeVideo?.channel}
        url={activeVideo?.url || ""}
        onClose={() => setActiveVideo(null)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1, backgroundColor: "transparent" },
  content: { paddingBottom: 32 },
  journeyLabel: {
    fontSize: 10,
    fontWeight: "800",
    color: CF.cyan,
    letterSpacing: 2,
    marginHorizontal: 16,
    marginTop: 4,
  },
  title: {
    fontSize: 28,
    fontWeight: "900",
    color: "#FFFFFF",
    marginHorizontal: 16,
    marginTop: 4,
    letterSpacing: -0.5,
    textShadowColor: "rgba(0,0,0,0.7)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 14,
    color: "#D4EEEB",
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 12,
    lineHeight: 21,
    fontWeight: "500",
  },
  langRow: {
    flexDirection: "row",
    gap: 8,
    marginHorizontal: 16,
    marginBottom: 18,
  },
  langChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.25)",
    backgroundColor: "rgba(255,255,255,0.06)",
  },
  langChipOn: {
    borderColor: CF.cyan,
    backgroundColor: "rgba(45, 212, 191, 0.18)",
  },
  langChipText: { color: "#B8D4D0", fontWeight: "700", fontSize: 13 },
  langChipTextOn: { color: "#FFFFFF" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: "800",
    color: CF.primaryBright,
    letterSpacing: 1.2,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  sectionGap: { marginTop: 8 },
  empty: {
    marginHorizontal: 16,
    marginBottom: 12,
    color: "#9CA3AF",
    fontSize: 13,
  },
  videoCard: {
    marginHorizontal: 16,
    marginBottom: 14,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "rgba(6, 12, 28, 0.72)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.12)",
  },
  thumbWrap: { position: "relative", width: "100%", height: 180 },
  thumb: { width: "100%", height: "100%" },
  thumbFallback: { backgroundColor: "#0F172A" },
  playBadge: {
    position: "absolute",
    alignSelf: "center",
    top: "42%",
    width: 54,
    height: 54,
    borderRadius: 27,
    backgroundColor: "rgba(0,0,0,0.55)",
    borderWidth: 2,
    borderColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    left: "50%",
    marginLeft: -27,
  },
  playBadgeText: { color: "#fff", fontSize: 18, marginLeft: 3 },
  videoMeta: { padding: 12 },
  videoTitle: { color: "#FFFFFF", fontWeight: "800", fontSize: 16, marginBottom: 4 },
  videoChannel: { color: CF.cyan, fontWeight: "700", fontSize: 12, marginBottom: 6 },
  videoSummary: { color: "#CBD5E1", fontSize: 13, lineHeight: 18 },
  readerBody: {
    padding: 16,
    paddingTop: 18,
    backgroundColor: "rgba(6, 12, 28, 0.85)",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    marginTop: -16,
  },
  backBtn: {
    marginBottom: 12,
    alignSelf: "flex-start",
    minHeight: touchUI.minHit,
    minWidth: touchUI.backMinWidth,
    paddingVertical: touchUI.backPadV,
    paddingHorizontal: touchUI.backPadH,
    borderRadius: 12,
    backgroundColor: "rgba(45, 212, 191, 0.14)",
    justifyContent: "center",
  },
  backBtnText: { color: CF.cyan, fontWeight: "800", fontSize: touchUI.backLabel },
  readerTitle: { fontSize: 24, fontWeight: "900", color: "#FFFFFF", marginBottom: 8 },
  metaRow: { flexDirection: "row", gap: 14, marginBottom: 8 },
  meta: { fontSize: 13, fontWeight: "700", color: CF.primaryBright },
  readerTagline: { fontSize: 15, color: "#E2F5F3", marginBottom: 16, lineHeight: 22 },
  articleBody: { fontSize: 15, color: "#CBD5E1", lineHeight: 24 },
  linkBtn: {
    marginTop: 20,
    alignSelf: "flex-start",
    backgroundColor: CF.cyan,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
  },
  linkBtnText: { color: "#042F2E", fontWeight: "800", fontSize: 14 },
});
